import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';

import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { Utils } from '../../../utils/utils';

export interface Scale {
  min: number;
  max: number;
  step: number;
}

export type Mode = 'view' | 'edit';

@EnableClassTelemetry()
@Component({
  selector: 'app-lex-horizontal-stepper',
  templateUrl: './lex-horizontal-stepper.component.html',
  styleUrls: ['./lex-horizontal-stepper.component.scss'],
  standalone: false,
})
export class LexHorizontalStepperComponent implements OnInit, OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() value: number;
  @Input() originalValue?: number;
  @Input() scale: Scale;
  @Input() size: 'small' | 'normal' | 'large' = 'small';
  @Input() allowNegative = false;
  @Input() allowFractional = false;
  @Input() decimals = 0;
  @Input() disabled = false;
  @Input() label?: string;
  @Input() canEdit = true;

  @Output() valueChange = new EventEmitter<number>();
  @Output() modeChange = new EventEmitter<Mode>();

  valueControl: UntypedFormControl;
  isEditMode = false;
  isChanged = false;

  innerValue: number;
  decimalsPipe: string;

  width: number;
  height: number;
  editorWidth: number;
  fontSize: number;

  constructor(formBuilder: UntypedFormBuilder) {
    this.valueControl = formBuilder.control('');
  }

  ngOnInit(): void {
    this.innerValue = this.value;
    this.setValueControlValue(this.innerValue);

    this.decimalsPipe = `1.${this.decimals}-${this.decimals}`;

    this.checkIsChanged();

    if (this.disabled || !this.canEdit) {
      this.valueControl.disable();
    }

    this.setSizes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value && !changes.value.firstChange) {
      this.innerValue = changes.value.currentValue;
      this.setValueControlValue(this.innerValue);

      this.checkIsChanged();
    }

    if (changes.scale && !changes.scale.firstChange) {
      this.setEditorWidth();
    }
  }

  onValueChange(source: 'minus' | 'plus' | 'inline'): void {
    let newValue: number;
    switch (source) {
      case 'inline':
        newValue = +this.valueControl.value;
        break;
      case 'minus':
        newValue = this.innerValue - this.scale.step;
        break;
      case 'plus':
        newValue = this.innerValue + this.scale.step;
        break;
    }

    if (newValue < this.scale.min) {
      newValue = this.scale.min;
    } else if (newValue > this.scale.max) {
      newValue = this.scale.max;
    }

    if (newValue === this.innerValue) {
      return;
    }

    this.setNewValue(newValue);

    this.checkIsChanged();
  }

  private setNewValue(newValue: number): void {
    this.innerValue = newValue;
    this.setValueControlValue(newValue);

    this.valueChange.emit(newValue);
  }

  onModeChanged(mode: Mode): void {
    this.isEditMode = mode === 'edit';

    this.modeChange.emit(mode);
  }

  private setEditorWidth(): void {
    let fontWidth: number;
    switch (this.size) {
      default:
      case 'small':
        fontWidth = 9;
        break;
      case 'normal':
        fontWidth = 10;
        break;
      case 'large':
        fontWidth = 11;
        break;
    }

    let characterCount = Math.max(Math.abs(this.scale.min), Math.abs(this.scale.max)).toString()
      .length;

    if (this.allowNegative) {
      characterCount++;
    }

    if (this.allowFractional) {
      characterCount += this.decimals + 1;
    }

    this.editorWidth = characterCount * fontWidth;
  }

  private setSizes(): void {
    this.setEditorWidth();

    let buttonSize: number;
    switch (this.size) {
      default:
      case 'small':
        {
          buttonSize = 30;
          this.fontSize = 14;
        }
        break;
      case 'normal':
        {
          buttonSize = 36;
          this.fontSize = 16;
        }
        break;
      case 'large':
        {
          buttonSize = 42;
          this.fontSize = 18;
        }
        break;
    }

    // 2 * 2 border
    this.width = 2 * buttonSize + this.editorWidth + 4;
    this.height = buttonSize + 4;
  }

  private checkIsChanged(): void {
    this.isChanged = this.originalValue !== undefined && this.originalValue !== this.innerValue;
  }

  private setValueControlValue(value): void {
    this.valueControl.setValue(Utils.round(value, this.decimals));
  }
}
